import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { cartService } from '../services/cartService';
import { paymentService } from '../services/paymentService';
import toast from 'react-hot-toast';

const CartContext = createContext();

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        if (user) {
            loadCart();
        } else {
            setCart({ items: [], total: 0 });
        }
    }, [user]);

    const loadCart = async () => {
        if (!user) return;

        setLoading(true);
        try {
            const { data, error } = await cartService.getCart(user.uid);
            if (data && !error) {
                setCart(data);
            } else if (error) {
                toast.error('Error loading cart');
            }
        } catch (error) {
            toast.error('Error loading cart');
        } finally {
            setLoading(false);
        }
    };

    const addToCart = async (item) => {
        if (!user) {
            toast.error('Please login to add items to cart');
            return;
        }

        if (!item || !item.id || !item.name || !item.price || !item.quantity) {
            toast.error('Invalid item data');
            return;
        }

        setLoading(true);
        try {
            const { data, error } = await cartService.addToCart(user.uid, item);
            if (data && !error) {
                setCart(data);
            } else if (error) {
                toast.error('Error adding to cart');
            }
        } catch (error) {
            toast.error('Error adding to cart');
        } finally {
            setLoading(false);
        }
    };

    const updateCartItem = async (itemId, quantity) => {
        if (!user) return;

        if (quantity < 0) {
            toast.error('Quantity cannot be negative');
            return;
        }

        setLoading(true);
        try {
            const { data, error } = await cartService.updateCartItem(user.uid, itemId, quantity);
            if (data && !error) {
                setCart(data);
                if (quantity === 0) {
                    toast.success('Item removed from cart');
                }
            } else if (error) {
                toast.error('Error updating cart');
            }
        } catch (error) {
            toast.error('Error updating cart');
        } finally {
            setLoading(false);
        }
    };

    const removeFromCart = async (itemId) => {
        if (!user) return;

        setLoading(true);
        try {
            const { data, error } = await cartService.removeFromCart(user.uid, itemId);
            if (data && !error) {
                setCart(data);
            } else if (error) {
                toast.error('Error removing from cart');
            }
        } catch (error) {
            toast.error('Error removing from cart');
        } finally {
            setLoading(false);
        }
    };

    const clearCart = async () => {
        if (!user) return;

        const confirmed = window.confirm('Are you sure you want to clear your entire cart?');
        if (!confirmed) return;

        setLoading(true);
        try {
            const { data, error } = await cartService.clearCart(user.uid);
            if (data && !error) {
                setCart(data);
                toast.success('Cart cleared');
            } else if (error) {
                toast.error('Error clearing cart');
            }
        } catch (error) {
            toast.error('Error clearing cart');
        } finally {
            setLoading(false);
        }
    };

    const createOrder = async (orderData) => {
        if (!user) {
            toast.error('Please login to place order');
            return { data: null, error: 'User not authenticated' };
        }

        if (!cart.items || cart.items.length === 0) {
            toast.error('Your cart is empty');
            return { data: null, error: 'Cart is empty' };
        }

        try {
            if (!orderData.shippingInfo || !orderData.shippingInfo.email) {
                toast.error('Missing shipping information');
                return { data: null, error: 'Missing shipping information' };
            }

            const result = await paymentService.createOrder(user.uid, {
                items: cart.items,
                total: cart.total,
                ...orderData
            });

            if (result.data && !result.error) {
                setCart({ items: [], total: 0 });
                toast.success('Order placed successfully!');
            }

            return result;
        } catch (error) {
            toast.error('Error creating order');
            return { data: null, error: error.message };
        }
    };

    const getCartCount = () => {
        return cart.items.reduce((count, item) => count + (item.quantity || 0), 0);
    };

    const getCartTotal = () => {
        return cart.items.reduce((total, item) => total + ((item.price || 0) * (item.quantity || 0)), 0);
    };

    const isItemInCart = (itemId) => {
        return cart.items.some(item => item.id === itemId);
    };

    const getItemQuantity = (itemId) => {
        const item = cart.items.find(item => item.id === itemId);
        return item ? item.quantity : 0;
    };

    const calculateShipping = async (country) => {
        if (!cart.items || cart.items.length === 0) {
            return { shippingCost: 0, error: 'Cart is empty' };
        }

        try {
            const result = await paymentService.calculateShipping(country, cart.items);
            return result;
        } catch (error) {
            return { shippingCost: 0, error: error.message };
        }
    };

    const value = {
        cart,
        loading,
        addToCart,
        updateCartItem,
        removeFromCart,
        clearCart,
        createOrder,
        getCartCount,
        getCartTotal,
        isItemInCart,
        getItemQuantity,
        calculateShipping,
        loadCart
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};